import { HashRouter, Outlet, Route, Routes, useLocation } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  ArcElement,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Backdrop, Box, CircularProgress, Stack, ThemeProvider } from "@mui/material";
import LeftMenu from "./components/LeftMenu";
import { theme } from "./lib/theme";
import TopHeader, { LogoCurrentHierarchy } from "./components/TopHeader";
import { Amplify } from "aws-amplify";
import { Hub } from "aws-amplify/utils";
import CommonDialog from "./components/CommonDialog";
import CommonDrawer from "./components/CommonDrawer";
import ConfirmDialog from "./components/ConfirmDialog";
import PreviewMediaDialog from "./components/PreviewMediaDialog";
import SnackMessage from "./components/SnackMessage";
import { ConfigContext } from "./containers/ConfigContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ClaimUser } from "./models/authorize.model";
import { getSelf, initialLoggedUser } from "./actions/authorize.action";
import { useAuthenticator } from "@aws-amplify/ui-react";
import CompanyStoresPage from "./pages/company.store.page";
import CompanyUsersPage from "./pages/company.user.page";
import { getUrlParam } from "./lib/helper";
import AuthenticatedPages from "./pages/authenticated.page";
import { FilterModel, ReportModel, ReviewsContext, getOptions } from "./models/report.model";
import { getReport, getReviews } from "./actions/report.action";
import NoResult from "./components/NoResult";
import { FilterForm } from "./containers/FilterContent";
import { sendClientHeightChange } from "./actions/common.action";
import ProductReportPage from "./pages/product.report.page";
import DetailsReportPage from "./pages/details.report.page";
import SubmitRecordPage from "./pages/submit.record.page";
import TextPage from "./pages/text.page";
import { getReportTranslation } from "./actions/setting.action";
import { ReportTranslation } from "./models/setting.model";
import { SwitchLanguageButton } from "./components/SwitchLanguageButton";
import { getChartConfig } from "./actions/company.chart.config.action";
import ChartsPage from "./pages/charts.page";
import ChartsPrintPage from "./pages/charts.print.page";
import ComplainsPage from "./pages/complains.page";

ChartJS.register(CategoryScale, ArcElement, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT || "",
      userPoolId: process.env.REACT_APP_USER_POOL_ID || "",
      signUpVerificationMethod: "code",
    },
  },
});

(window as any).showAlfaFilter = () => {
  const chart = (window as any).ChartMessage;
  window.webApp
    .toggleDialog({
      open: true,
      title: "Filter",
      element: <FilterForm />,
      noCancel: true,
      okText: "Cancel",
      disableFullScreen: true,
      onOk: async () => {
        chart && chart.postMessage(JSON.stringify({ height: document.body.clientHeight }));
        return;
      },
    })
    .catch(() => {
      chart && chart.postMessage(JSON.stringify({ height: document.body.clientHeight }));
    });
};

function App() {
  const [authUser, setAuthUser] = useState<ClaimUser | undefined>();
  const [filter, setFilter] = useState<FilterModel | undefined>();
  const [data, setData] = useState<{ main: ReportModel; dates: ReportModel[]; ranges: string[] } | undefined>();
  const [reviews, setReviews] = useState<ReviewsContext | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const { authStatus } = useAuthenticator();
  const [reportTranslation, setReportTranslation] = useState<ReportTranslation | undefined>();

  useEffect(() => {
    const hubListenerCancel = Hub.listen("auth", (data) => {
      switch (data.payload.event) {
        case "signedIn":
          loadUser();
          break;
        case "signedOut":
          setAuthUser(undefined);
          break;
        default:
          console.warn("unhandled event", data);
          break;
      }
    });
    if (authStatus === "authenticated") {
      loadUser();
    }
    return () => hubListenerCancel();
  }, [authStatus]);

  const loadChartConfig = async () => {
    if (authUser && authUser.company?.id && !authUser.chartConfig) {
      setLoading(true);
      try {
        const config = await getChartConfig();
        setAuthUser({ ...authUser, chartConfig: config });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const loadUser = async () => {
    const u = await initialLoggedUser();
    setAuthUser(u);
    if (u) {
      const { company, allowedHierarchies, surveyEvents } = await getSelf();
      setAuthUser({ ...u, company, allowedHierarchies, surveyEvents });
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const { hierarchyFilter, ...rest } = filter || {};
      const param = {
        id: hierarchyFilter || getOptions(authUser)?.[0]?.value,
        ...rest,
      };
      const [res0, res1] = await Promise.allSettled([getReport(param), getReviews(param)]);
      const report = res0.status === "fulfilled" ? res0.value : undefined;
      const reviews = res1.status === "fulfilled" ? res1.value : undefined;
      if (report) {
        setData(report);
      }
      if (reviews) {
        setReviews({
          data: reviews.reviews,
          offset: reviews.offset,
          total: reviews.total,
        });
      }
    } catch (error) {
      // some error happened;
      console.error(error);
    }
    sendClientHeightChange();
    setLoading(false);
  };

  useEffect(() => {
    if (authUser?.company) {
      loadChartConfig();
      loadReport();
    }
    const chart = (window as any).ChartMessage;
    chart && chart.postMessage(JSON.stringify({ filter: filter ? filter : null }));
  }, [filter, authUser?.company?.id]);

  const localized = useCallback((key: string): string => {
    return reportTranslation?.translations?.[key] || key;
  }, [reportTranslation]);

  const onSwitchLanguage = (code: string) => {
    if (code == "en") {
      setReportTranslation(undefined);
      return;
    }
    setLoading(true);
    getReportTranslation(code)
      .then((res) => setReportTranslation(res.reportTranslation))
      .finally(() => setLoading(false));
  };

  const mobile = getUrlParam("mobile");

  const configValue = useMemo(() => {
    return {
      loggedUser: authUser,
      setLoggedUser: setAuthUser,
      filter,
      setFilter,
      data,
      reviews,
      setReviews,
      localized,
      loading,
      setLoading,
    }
  }, [
    authUser,
    filter,
    data,
    reviews,
    localized,
    loading,
  ])

  return (
    <ConfigContext.Provider value={configValue}>
      <ThemeProvider theme={theme}>
        <AuthenticatedPages>
          <HashRouter>
            <Routes>
              <Route path="/charts/print/:url" element={<ChartsPrintPage />} />
              <Route
                element={
                  <>
                    {mobile ? (
                      <div className="padding-large" style={{ height: "100vh", overflow: "auto" }}>
                        <Top onSwitchLanguage={onSwitchLanguage} />
                        <Outlet />
                      </div>
                    ) : (
                      <>
                        <Stack direction="row">
                          <LeftMenu />
                          <div className="flex-1 padding-large relative" style={{ overflow: "auto" }}>
                            <TopHeader onSwitchLanguage={onSwitchLanguage} />
                            <Outlet />
                          </div>
                        </Stack>
                      </>
                    )}
                  </>
                }
              >
                <Route path={"/"} element={<ProductReportPage />} />
                <Route path={"/details-report"} element={<DetailsReportPage />} />
                <Route path="/stores" element={<CompanyStoresPage />} />
                <Route path="/users" element={<CompanyUsersPage />} />
                <Route path="/records" element={<SubmitRecordPage />} />
                <Route path={"/text"} element={<TextPage />} />
                <Route path="/charts/:url" element={<ChartsPage />} />
                <Route path="/closeloop" element={<ComplainsPage />} />
                <Route path="*" element={<NoResult title="Page Not Found" />} />
              </Route>
            </Routes>
          </HashRouter>
          <CommonDrawer />
          <CommonDialog />
          <ConfirmDialog />
          <PreviewMediaDialog />
        </AuthenticatedPages>
        <SnackMessage />
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </ThemeProvider>
    </ConfigContext.Provider>
  );
}

function Top(props: { onSwitchLanguage: (code: string) => void }) {
  const location = useLocation();
  useEffect(() => {
    sendClientHeightChange();
  }, [location.pathname]);
  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Stack direction={"row"} justifyContent={"space-between"} alignItems={'flex-end'} sx={{ pr: 2 }}>
        <LogoCurrentHierarchy />
        <SwitchLanguageButton onSwitchLanguage={props.onSwitchLanguage} />
      </Stack>
    </Box>
  );
}

export default App;
